import React from 'react';

import { Checkbox, Flex, Form, Input, List, message, Row, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { stringToBoolean } from '@app/services/stringToBoolean';
import { AddressAutocomplete } from '@components/AntFields';
import { ListItem } from '@components/ListItem';
import { useGetWarehouseQuery, useGetWarehouseSettingsQuery, useUpdateWarehouseQuery } from '@pages/Warehouses/hooks';
import { useWarehouse } from '@pages/Warehouses/pages/Single/WarehouseProvider';
import { getWarehouseAddress } from '@pages/Warehouses/services';

import { IFormValues } from './interfaces';
import { ValidationSchema } from './validations';

export const Settings: React.FC = () => {
  const epdEnabled = stringToBoolean(process.env.REACT_APP_FEATURE_EPD);
  const commonT = useTranslation(NSKeys.common);
  const [form] = Form.useForm<IFormValues>();
  const params = useParams<{ id: string }>();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const validationT = useTranslation(NSKeys.validation);
  const autocompleteT = useTranslation(NSKeys.autocomplete);
  const getErrorMessage = useApiErrorMessage();
  const { isEditing, setEditDisabled, setEditing } = useWarehouse();

  const { data, isLoading } = useGetWarehouseQuery(params?.id || '');
  const { mutate, status: updateStatus } = useUpdateWarehouseQuery();
  const { data: settings, isLoading: isSettingsLoading } = useGetWarehouseSettingsQuery(params?.id || '');

  React.useEffect(() => {
    const address = data?.address
      ? {
          ...data.address,
          street_number: data.address.street_number || null,
          place_name: data.address.place_name || null,
          country: data.address.country_name,
          region: data.address.region_name,
          city: data.address.city_name,
        }
      : undefined;
    form.setFieldsValue({ name: data?.name, address, epd_enabled: !!data?.epd_enabled });
    setEditDisabled(!data?.$permissions?.update);
  }, [data, isEditing]);

  React.useEffect(() => {
    if (settings) {
      form.setFieldsValue({ wms_base_url: settings?.api_url, wms_api_key: settings?.api_key });
    }
  }, [settings, isEditing]);

  React.useEffect(() => {
    if (updateStatus === 'success') {
      setEditing(false);
    }
  }, [updateStatus]);

  const handleSubmit = (values: IFormValues) => {
    mutate(
      {
        uid: params.id || '',
        data: {
          name: values.name,
          ...(epdEnabled && { epd_enabled: !!values.epd_enabled }),
          ...(values.address?.provider_address_uid && { address: getWarehouseAddress(values.address) }),
        },
      },
      {
        onError: (err) => {
          const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
          form.setFields(pointerErrors);
          if (!pointerErrors.length) {
            message.error(getErrorMessage(err));
          }
        },
      }
    );
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  if (isLoading || isSettingsLoading) {
    return (
      <Flex justify={'center'} style={{ padding: '40px 0' }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <React.Fragment>
      {isEditing ? (
        <Form<IFormValues>
          form={form}
          name="createWarehouse"
          id="settingsWarehouse"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 8 }}
          disabled={updateStatus === 'loading'}
          onFinish={handleSubmit}>
          <Form.Item<IFormValues> name="name" required label={warehousesT.t('name')} rules={[yupSync]}>
            <Input placeholder={warehousesT.t('namePlaceholder')} />
          </Form.Item>
          <Form.Item<IFormValues> name="address" rules={[yupSync]} wrapperCol={{ span: 24 }}>
            <AddressAutocomplete
              form={form}
              name={['address']}
              rules={[]}
              required={false}
              hasError={!!form.getFieldError('address').length}
              wrapperCol={{ span: 8 }}
            />
          </Form.Item>
          {epdEnabled && (
            <Form.Item name="epd_enabled" valuePropName="checked" label={warehousesT.t('epdEnabled')}>
              <Checkbox />
            </Form.Item>
          )}
          <Form.Item name="wms_base_url" label={warehousesT.t('wms_base_url')} rules={[yupSync]}>
            <Input disabled placeholder={warehousesT.t('wms_api_keyPlaceholder')} />
          </Form.Item>
          <Form.Item name="wms_api_key" label={warehousesT.t('wms_api_key')} rules={[yupSync]}>
            <Input disabled placeholder={warehousesT.t('wms_api_keyPlaceholder')} />
          </Form.Item>
          <Row></Row>
        </Form>
      ) : (
        <List style={{ maxWidth: 600 }} loading={isLoading}>
          <ListItem title={warehousesT.t('name')} description={data?.name} />
          <ListItem title={autocompleteT.t('label')} description={data?.address?.as_string || '—'} />
          {epdEnabled && (
            <ListItem title={warehousesT.t('epdEnabled')} description={commonT.t(data?.epd_enabled ? 'yes' : 'no')} />
          )}
          <ListItem title={warehousesT.t('wms_base_url')} description={settings?.api_url} />
          <ListItem title={warehousesT.t('wms_api_key')} description={settings?.api_key} />
        </List>
      )}
    </React.Fragment>
  );
};
